/**
 * Project state management with Zustand
 * Handles project CRUD operations and state
 * Context: Global state for project management in Video Lab
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { api } from '../lib/api';
import type { Project } from '../lib/types';

// Store interface for project management
interface ProjectStore {
  projects: Project[];
  currentProject: Project | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  loadProjects: () => Promise<void>;
  createProject: (videoUrl: string) => Promise<Project>;
  updateProject: (id: string, updates: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  setCurrentProject: (project: Project | null) => void;
}

export const useProjectStore = create<ProjectStore>()(
  devtools(
    (set) => ({
      projects: [],
      currentProject: null,
      isLoading: false,
      error: null,

      loadProjects: async () => {
        set({ isLoading: true, error: null });
        
        try {
          const response = await api.getProjects();
          
          if (response.success && response.data) {
            set({ projects: response.data, isLoading: false });
          } else {
            throw new Error(response.error || 'Failed to load projects');
          }
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Unknown error',
            isLoading: false 
          });
        }
      },

      createProject: async (videoUrl: string) => {
        set({ isLoading: true, error: null });
        
        try {
          // Generate a default project name from video URL or timestamp
          const defaultName = `Video Lab Project - ${new Date().toLocaleDateString()}`;
          
          const response = await api.createProject(defaultName, videoUrl);
          
          if (response.success && response.data) {
            const newProject = response.data;
            
            set(state => ({
              projects: [newProject, ...state.projects],
              currentProject: newProject,
              isLoading: false,
            }));
            
            return newProject;
          } else {
            throw new Error(response.error || 'Failed to create project');
          }
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Unknown error',
            isLoading: false 
          });
          throw error;
        }
      },

      updateProject: async (id: string, updates: Partial<Project>) => {
        set({ isLoading: true, error: null });
        
        try {
          const response = await api.updateProject(id, updates);
          
          if (response.success && response.data) {
            const updatedProject = response.data;
            
            set(state => ({
              projects: state.projects.map(p => 
                p.id === id ? updatedProject : p
              ),
              currentProject: state.currentProject?.id === id 
                ? updatedProject 
                : state.currentProject,
              isLoading: false,
            }));
          } else {
            throw new Error(response.error || 'Failed to update project');
          }
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Unknown error',
            isLoading: false 
          });
        }
      },

      deleteProject: async (id: string) => {
        set({ isLoading: true, error: null });
        
        try {
          const response = await api.deleteProject(id);
          
          if (response.success) {
            set(state => ({
              projects: state.projects.filter(p => p.id !== id),
              currentProject: state.currentProject?.id === id 
                ? null 
                : state.currentProject,
              isLoading: false,
            }));
          } else {
            throw new Error(response.error || 'Failed to delete project');
          }
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Unknown error',
            isLoading: false 
          });
        }
      },

      setCurrentProject: (project: Project | null) => {
        set({ currentProject: project });
      },
    }),
    {
      name: 'project-store',
    }
  )
);